/**
 * Filters and updates an array of upsell items based on the cart state.
 * This is responsible for updating the cart when upsell items (including GWP) need to be added or removed.
 *
 * @param {Array} upsells - Array of upsell items. Each item should have an 'id' and 'maxQuantityAccessible'.
 * @param {Object} cartState - The current state of the cart, should contain an 'items' array.
 * @returns {Array} - Array of items to update. Items not in cartState.items or with different quantities are included.
 */
export const getItemsToUpdate = (upsells, cartState) => {
  const closedUpsellArr = JSON.parse(
    sessionStorage.getItem('ups_items') || '[]',
  );

  // get manually removed auto-add items from session storage
  // when an auto-add item has been manually removed, it should not be re-added automatically
  const removedAutoAddThresholdItemId = sessionStorage.getItem('auto_add_threshold_removed') ? parseInt(sessionStorage.getItem('auto_add_threshold_removed'), 10) : ''
  const removedAutoAddProdTypeItemId = sessionStorage.getItem('auto_add_product_type_removed') ? parseInt(sessionStorage.getItem('auto_add_product_type_removed'), 10) : ''

  // Reset removed GWP items when cart is empty
  if (cartState?.items?.length === 0) {
    sessionStorage.setItem('auto_add_threshold_removed', '');
    sessionStorage.setItem('auto_add_product_type_removed', '');
  }

  // upsells is an array of objects and each object is destructured to get its properties
  // upsells are set in upsells.js with setUpsells()
  // and then upsells are passed to getItemsToUpdate() in cart-context.js
  return upsells
    .filter(({
      id,
      maxQuantityAccessible,
      type,
      showQuantitySelector,
      automaticallyDelete,
      productTypesRequired,
      requiresExactQtyToTypeMatch,
      qtyRequired
    }) => {
      const isManuallyRemoved = id === removedAutoAddThresholdItemId || id === removedAutoAddProdTypeItemId

      if (!automaticallyDelete || isManuallyRemoved) return false;

      const upsellItem = cartState.items?.find((item) => item.id === id);

      // maxQuantityAccessible is calculated in upsells.js with calculateMaxQuantityAccessible()
      // it is the quantity of the upsell item that can be added to the cart
      // if maxQuantityAccessible is 0, either the upsell item will be removed from the cart or this returns false and the cart is not updated

      if (type.includes('carousel')) {
        return upsellItem?.quantity > maxQuantityAccessible;
      }

      if (type.includes('threshold')) {
        // returning true from here means the cart should be updated and false means it won't be updated

        // If threshold is no longer met and cart item exists, remove the gift
        if (upsellItem && maxQuantityAccessible === 0) {
          return true;
        }

        // If threshold is no longer met and cart item doesn't exist, don't update
        if (!upsellItem && maxQuantityAccessible === 0) {
          return false;
        }

        // If no cart item exists but threshold is met, add the gift
        if (!upsellItem && maxQuantityAccessible > 0) {
          return true;
        }

        // If threshold is still met but quantity matches, don't update
        if (upsellItem && upsellItem.quantity === maxQuantityAccessible) {
          return false;
        }

        // If threshold is met again and the cart item exists, update the quantity of the cart item
        if (upsellItem && upsellItem.quantity < maxQuantityAccessible) {
          return true;
        }

      }

      if (type.includes('product-type') && requiresExactQtyToTypeMatch) {
        const typesInCart = cartState.items
          .filter(item => productTypesRequired.includes(item.product_type.toLowerCase()))
          .reduce((total, item) => total + item.quantity, 0);

        if (parseInt(qtyRequired, 10) !== typesInCart) return false
      }

      return (
        (!upsellItem && maxQuantityAccessible > 0 && !closedUpsellArr.includes(id * 0.22)) ||
        (upsellItem &&
          !showQuantitySelector &&
          upsellItem.quantity !== maxQuantityAccessible &&
          !closedUpsellArr.includes(id * 0.22)) ||
        (upsellItem &&
          showQuantitySelector &&
          upsellItem.quantity > maxQuantityAccessible &&
          !closedUpsellArr.includes(id * 0.22))
      );
    })
    .map(({ maxQuantityAccessible, ...rest }) => ({
      ...rest,
      quantity: maxQuantityAccessible,
    }));
}

/**
 * Calculates the maximum quantity accessible for a given upsell promotion.
 *
 * @param {Object} upsellPromo - The upsell promotion object containing various properties like 'type', 'threshold', etc.
 * @param {Object} cartState - The current cart state object, should contain an 'items' array with cart items.
 * @param {Array} excludeVariants - An array of variant IDs that should be excluded from the calculation.
 * @returns {number} - The maximum accessible quantity based on the promotion rules.
 */

export const calculateMaxQuantityAccessible = (
  upsellPromo,
  cartState,
  excludeVariants) => {

  // "type" is the block.type from cart.liquid
  if (upsellPromo.type.includes('threshold')) {
    const threshold = parseInt(upsellPromo.threshold, 10) * 100;

    // Calculate total price for items in cart to determine if the promo threshold has been met ("amount" is accumulator)
    // if a line item is excluded, then the price of that item does not count towards the promo threshold
    const calculatedTotalPrice = cartState.items.reduce((amount, item) => {

      // the "excludeVariants" list comes from upsells.js
      // "excludeVariants" is an array of variant IDs that should not count towards the threshold
      // when the "excludeVariants" array contains the line item ID, the item is excluded from the threshold calculation
      if (excludeVariants.includes(item.id)) return amount;

      if (upsellPromo.type.includes('carousel')) {
        const productType = item.product_type.toLowerCase();
        let typesToExclude;

        // the "product_exclusions_by_type" list comes from the Theme Customizer - "Carousel (threshold)"
        if (!upsellPromo.product_exclusions_by_type?.includes(',')) {
          typesToExclude = upsellPromo.product_exclusions_by_type?.toLowerCase().trim() || '';
        }
        else {
          const typesToExcludeArray = upsellPromo.product_exclusions_by_type.split(',');
          typesToExclude = typesToExcludeArray.map(type => type.toLowerCase().trim());
        }

        // if "typesToExclude" array contains the line item product type, the item is excluded from the threshold calculation
        if (typesToExclude.includes(productType)) {
          return amount;
        }
      }

      // GWP
      if (upsellPromo.type.includes('auto-add')) {
        let excludeLineItem = false;
        if (item.properties?._product_tags) {
          // "product_exclusions_by_tag" is an array that comes from a "gwp-discounts" metaobject entry
          upsellPromo.product_exclusions_by_tag.forEach(tag => {
            // "_product_tags" is a string
            if (item.properties._product_tags.includes(tag)) {
              // if the line item product has a tag that matches a tag in "product_exclusions_by_tag", exclude it from the threshold calculation
              excludeLineItem = true;
            }
          })
        }

        if (excludeLineItem) return amount;
      }

      return item.final_line_price + amount;
    }, 0);

    // number of times cart total has met threshold (i.e. cart total >= threshold)
    const numberOfTimesThresholdMet = Math.floor(calculatedTotalPrice / threshold);
    const gwpThresholdHasBeenMet = numberOfTimesThresholdMet > 0;

    if (!gwpThresholdHasBeenMet) return 0;

    // qty of GWP to add to the cart each time cart total meets or exceeds threshold
    const gwpQtyPerThreshold = parseInt(upsellPromo.gwp_qty_to_add, 10);

    // max number of times the promo can be applied- i.e. the number of times a GWP can be added to the cart
    const gwpMaxUses = upsellPromo.gwp_max_uses ? parseInt(upsellPromo.gwp_max_uses, 10) : null;

    // if gwpMaxUses has been set to 0, or numberOfTimesThresholdMet exceeds gwpMaxUses, then don't add any GWP
    if (gwpMaxUses && numberOfTimesThresholdMet > gwpMaxUses) return "GWP max uses exceeded";

    const qtyToAdd = numberOfTimesThresholdMet * gwpQtyPerThreshold;
    return qtyToAdd;
  }

  // "type" is the block.type from cart.liquid
  if (upsellPromo.type.includes('product-type')) {
    // NOTE: for GWP, "product-type" actually refers to product tags, not product types - see cart.liquid for more info
    // "quantity_required" is the number of items, of a specific type or with a specific tag, required to be in the cart to trigger the promo
    const quantityRequired = parseInt(upsellPromo.quantity_required, 10);

    if (upsellPromo.type.includes('carousel')) {
      // Count items in cart of a specific type
      const typesInCart = cartState.items
        .filter(item => upsellPromo.product_types_required.includes(item.product_type.toLowerCase()))
        .reduce((total, item) => total + item.quantity, 0);

      return Math.floor(typesInCart / quantityRequired);
    }

    // GWP
    if (upsellPromo.type.includes('auto-add')) {
      // number of items in cart with a specific tag
      let itemsWithTag = 0;

      // find all products in cart with a specific tag
      cartState.items.forEach((item) => {
        if (item.properties?._product_tags) {
          // eslint-disable-next-line no-plusplus
          for (let index = 0; index < upsellPromo.product_tags_required.length; index++) {
            // "_product_tags" is a string
            if (item.properties._product_tags.includes( upsellPromo.product_tags_required[index] )) {
              // if the line item product has a tag that matches a tag in "product_tags_required", include it in the count
              itemsWithTag += item.quantity;
              return;
            }
          }
        }
      });

      // number of products with a required tag found in the cart
      const numberOfTimesThresholdMet = Math.floor(itemsWithTag / quantityRequired);
      const gwpThresholdHasBeenMet = numberOfTimesThresholdMet > 0;

      if (!gwpThresholdHasBeenMet) return 0;

      // qty of GWP to add to the cart each time threshold met
      const gwpQtyPerThreshold = parseInt(upsellPromo.gwp_qty_to_add, 10);
      // max number of times the promo can be applied- i.e. the number of times a GWP can be added to the cart
      const gwpMaxUses = upsellPromo.gwp_max_uses ? parseInt(upsellPromo.gwp_max_uses, 10) : null;

      // if gwpMaxUses has been set to 0, or numberOfTimesThresholdMet exceeds gwpMaxUses, then don't add any GWP
      if (gwpMaxUses && numberOfTimesThresholdMet > gwpMaxUses) return "GWP max uses exceeded";

      const qtyToAdd = numberOfTimesThresholdMet * gwpQtyPerThreshold;
      return qtyToAdd;
    }
  }

  return 0;
};

